import { Injectable } from '@angular/core';
import { CustomProduct } from '@product/const';
import { ImageGroup, Images } from '@spartacus/core';
import { JsonLdBuilder } from '@spartacus/storefront';
import * as htmlparser2 from 'htmlparser2';
import { Observable, of } from 'rxjs';

/**
 * Формирует базовые структурированные данные товара, см. https://schema.org/product.
 * Включает артикул (sku), название, описание, бренд и все изображения товара.
 */
@Injectable({
  providedIn: 'root',
})
export class CustomJsonLdBaseProductBuilder implements JsonLdBuilder<CustomProduct> {
  /**
   * Собирает итоговый объект структурированных данных товара, объединяя
   * результаты базовых полей, изображений и бренда.
   */
  build(product: CustomProduct): Observable<any> {
    return of({
      ...this.getProductBase(product),
      ...this.getProductImage(product),
      ...this.getProductBrand(product),
    });
  }

  /**
   * Возвращает все доступные изображения товара в виде массива ссылок.
   */
  private getProductImage(product: CustomProduct): { image?: string[] } {
    const images: Images | undefined = product.images;
    if (!images) {
      return {};
    }

    const urls = new Set<string>();

    const addImage = (group?: ImageGroup): void => {
      const url = group?.['zoom']?.url;
      if (url) {
        urls.add(url);
      }
    };

    const primary = images.PRIMARY;
    if (Array.isArray(primary)) {
      primary.forEach(addImage);
    } else {
      addImage(primary);
    }

    const gallery = images.GALLERY;
    if (Array.isArray(gallery)) {
      gallery.forEach(addImage);
    } else {
      addImage(gallery);
    }

    return urls.size ? { image: Array.from(urls) } : {};
  }

  /**
   * Возвращает базовые поля товара: артикул (sku), название и описание.
   *
   * Описание предварительно очищается от HTML-тегов и обрезается
   * до первого полного предложения после 150 символов.
   */
  private getProductBase(product: CustomProduct): PlainObject {
    const result: PlainObject = { sku: product.code };
    if (product.name) {
      result.name = product.name;
    }
    if (product.description) {
      result.description = this.truncateDescription(this.stripHtml(product.description));
    }

    return result;
  }

  /**
   * Обрезает переданный текст до первого полного предложения (заканчивающегося точкой),
   * найденного после указанной минимальной длины.
   *
   * Если текст короче минимальной длины, или точка после неё не найдена,
   * возвращается исходный текст без изменений.
   */
  private truncateDescription(text: string, minLength = 150): string {
    if (text.length <= minLength) {
      return text;
    }
    const periodIndex = text.indexOf('.', minLength);

    return periodIndex !== -1 ? text.slice(0, periodIndex + 1) : text;
  }

  /**
   * Удаляет HTML-теги из переданной строки и схлопывает лишние пробелы возвращает очищенный текст.
   */
  private stripHtml(htmlStr: string): string {
    const doc = htmlparser2.parseDocument(htmlStr);

    return htmlparser2.DomUtils.textContent(doc).replace(/\s+/g, ' ').trim();
  }

  /**
   * Возвращает объект бренда товара, найденный среди характеристик (classifications)
   */
  private getProductBrand(product: CustomProduct): PlainObject {
    const brand = product.classifications
      ?.map((classification) => classification.features || [])
      .flat()
      .filter((feature) => feature.name === 'Бренд')
      .map((feature) => feature.featureValues || [])
      .flat()
      .map((featureValue) => featureValue.value)
      .find(Boolean);

    if (brand) {
      return {
        brand: {
          '@type': 'Brand',
          name: brand,
        },
      };
    }

    return {};
  }
}
